// OCI V2.19 — Canonical access tiers (mirror of backend oci_v2_19_roles.get_access_tier).
// ONE source of truth the frontend uses for routing, landing and dashboard selection.

export const TIER_ORDER = [
  "platform_owner", "owner", "national_manager", "regional_manager",
  "campaign_manager", "team_leader", "fundraiser",
];

export const TIER_LABELS = {
  platform_owner: "Platform Owner",
  owner: "Owner",
  national_manager: "National Manager",
  regional_manager: "Regional Manager",
  campaign_manager: "Campaign Manager",
  team_leader: "Team Leader",
  fundraiser: "Fundraiser",
};

const LEADERSHIP_TIERS = new Set([
  "platform_owner", "owner", "national_manager",
  "regional_manager", "campaign_manager", "team_leader",
]);
const OWNER_TIERS = new Set(["platform_owner", "owner"]);
const MGMT_TEAM_ROLES = new Set([
  "national_manager", "regional_manager", "campaign_manager", "team_leader",
]);

export function getAccessTier(user) {
  const role = (user?.role || "").toLowerCase();
  const tr = (user?.teamRole || "").toLowerCase();
  if (role === "platform_owner") return "platform_owner";
  if (role === "owner") return "owner";
  if (role === "manager") {
    if (MGMT_TEAM_ROLES.has(tr)) return tr;
    if (tr === "area_manager") return "regional_manager";
    return "campaign_manager";
  }
  if (role === "fundraiser") {
    if (MGMT_TEAM_ROLES.has(tr)) return tr;
    if (tr === "area_manager") return "regional_manager";
    return "fundraiser";
  }
  return role; // charity_user / client / unknown
}

export const isLeadership = (user) => LEADERSHIP_TIERS.has(getAccessTier(user));
export const isOwnerTier = (user) => OWNER_TIERS.has(getAccessTier(user));
export const isTeamLeaderTier = (user) => getAccessTier(user) === "team_leader";
export const isClassicManager = (user) => (user?.role || "").toLowerCase() === "manager";

// Elevated fundraiser leader: role=fundraiser but a management teamRole.
// These users cannot safely load owner-scoped widgets, so they get the
// self-contained Team-Leader management dashboard.
export const isElevatedFundraiserLeader = (user) =>
  (user?.role || "").toLowerCase() === "fundraiser" && isLeadership(user);

// OCI V2.22A — everyone enters through fundraiser capability; leadership users
// keep access to their OWN fundraiser self-service (Records/Schedule/Passport/
// Training/Conversion) when they intentionally switch into Fundraiser Workspace.
export const canFundraiserSelfService = (user) =>
  (user?.role || "").toLowerCase() === "fundraiser" || isLeadership(user);

// Who sees the restricted Team-Leader dashboard at /owner:
//  - any team_leader tier (regardless of underlying role)
//  - any elevated-fundraiser leader (role=fundraiser + mgmt teamRole)
export const useTeamLeaderDashboard = (user) =>
  isLeadership(user) && (isTeamLeaderTier(user) || isElevatedFundraiserLeader(user));

export function landingPathFor(user) {
  const role = (user?.role || "").toLowerCase();
  if (role === "platform_owner") return "/platform";
  if (role === "charity_user") return "/charity";
  if (role === "client") return "/client";
  if (isLeadership(user)) return "/owner";
  return "/dashboard";
}
